import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "./i18n";
import { AUDIENCES } from "./data/audiences";
import { track } from "./analytics";
import "./AudienceTabs.css";

/**
 * "Who we serve" — one interactive switcher instead of four stacked sections.
 * Content lives in src/data/audiences.ts. Full keyboard support (arrow keys).
 */

const HEAD = {
  en: { label: "Who We Serve", title: "One team. Four ways to plug in.", sub: "Homes, buildings, businesses and community groups — pick yours." },
  es: { label: "A Quién Servimos", title: "Un equipo. Cuatro maneras de conectar.", sub: "Hogares, edificios, negocios y grupos comunitarios — elige el tuyo." },
};

const AudienceTabs: React.FC = () => {
  const { lang } = useLang();
  const H = HEAD[lang];
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const select = (i: number) => {
    setActive(i);
    track("audience_selected", { audience: AUDIENCES[i].id });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const next = e.key === "ArrowRight"
      ? (active + 1) % AUDIENCES.length
      : (active - 1 + AUDIENCES.length) % AUDIENCES.length;
    select(next);
    tabRefs.current[next]?.focus();
  };

  const a = AUDIENCES[active];

  return (
    <section id="services" className="ee-section at-section">
      <div className="ee-container">
        <div className="ee-section-header" style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 40px" }}>
          <div className="ee-section-label" style={{ justifyContent: "center" }}>{H.label}</div>
          <h2 className="ee-section-title">{H.title}</h2>
          <p className="ee-section-sub" style={{ margin: "0 auto" }}>{H.sub}</p>
        </div>

        <div className="at-tabs" role="tablist" aria-label={H.label} onKeyDown={onKeyDown}>
          {AUDIENCES.map((aud, i) => (
            <button
              key={aud.id}
              ref={(el) => { tabRefs.current[i] = el; }}
              role="tab"
              id={`at-tab-${aud.id}`}
              aria-selected={i === active}
              aria-controls={`at-panel-${aud.id}`}
              tabIndex={i === active ? 0 : -1}
              className={`at-tab${i === active ? " on" : ""}`}
              onClick={() => select(i)}
            >
              <span className="at-tab-ico" aria-hidden="true">{aud.icon}</span>
              {aud.tab[lang]}
            </button>
          ))}
        </div>

        <div
          className="at-panel"
          role="tabpanel"
          id={`at-panel-${a.id}`}
          aria-labelledby={`at-tab-${a.id}`}
          key={a.id}
        >
          <div className="at-panel-main">
            <h3>{a.title[lang]}</h3>
            <p className="at-intro">{a.intro[lang]}</p>
            <Link to={a.cta.to} className="ee-btn ee-btn-green" onClick={() => track("pickup_cta_clicked", { source: `audience_${a.id}` })}>
              {a.cta.label[lang]}
            </Link>
          </div>
          <ul className="at-points">
            {a.points.map((p) => (
              <li key={p.en}>{p[lang]}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default AudienceTabs;
